/**
 * Month by Month: the workbook's Month by Month tabs -- ship-to > SKU down the
 * side, the fiscal months across. The workbook stacks four rows per SKU (this
 * year, last year, variance, %); here one switch picks the number and every row
 * shows it, so the table stays one row per line. The months shown are the
 * report's (Quarter / Month filters, never past the last complete month).
 */

import { useMemo, useState } from 'react'

import { Card, Empty } from '../shared/Card'
import type { Keyed } from '../shared/rows'
import type { Labels } from './CompareTable'
import { amount, percent, tone } from './format'
import { buildMonthTree, MONTHS, sumMonths, visible } from './tree'
import type { GroupNode, Level, MonthFigures } from './tree'

const MONTH_LAYOUTS: readonly { key: string; label: string; levels: readonly Level[] }[] = [
  { key: 'st-sku', label: 'Ship-to › SKU', levels: ['ship_to', 'sku'] },
  { key: 'eu-st-sku', label: 'End user › Ship-to › SKU', levels: ['end_user', 'ship_to', 'sku'] },
  { key: 'sku-st', label: 'SKU › Ship-to', levels: ['sku', 'ship_to'] },
  { key: 'pl-sku', label: 'Product line › SKU', levels: ['product_line', 'sku'] },
]

type Measure = 'cy' | 'py' | 'var_py' | 'pct_py' | 'plan' | 'var_plan'

function cell(figures: MonthFigures, measure: Measure, months: readonly number[]): number | null {
  const total = (series: readonly number[]) => months.reduce((s, m) => s + (series[m] ?? 0), 0)
  const cy = total(figures.cy)
  const py = total(figures.py)
  const plan = total(figures.plan)
  switch (measure) {
    case 'cy':
      return cy
    case 'py':
      return py
    case 'plan':
      return plan
    case 'var_py':
      return cy - py
    case 'var_plan':
      return cy - plan
    case 'pct_py':
      return py === 0 ? null : ((cy - py) / Math.abs(py)) * 100
  }
}

export function MonthByMonth({
  lines,
  labels,
  monthsShown,
}: {
  lines: readonly Keyed[]
  labels: Labels
  monthsShown: readonly number[]
}) {
  const [layout, setLayout] = useState('st-sku')
  const [measure, setMeasure] = useState<Measure>('var_py')
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const chosen = MONTH_LAYOUTS.find((candidate) => candidate.key === layout) ?? MONTH_LAYOUTS[0]
  const levels = chosen?.levels ?? ['ship_to', 'sku']
  const tree = useMemo(() => buildMonthTree(lines, levels), [lines, levels])
  const grand = useMemo(() => sumMonths(tree.map((node) => node.figures)), [tree])

  const measures: readonly { key: Measure; label: string }[] = [
    { key: 'cy', label: labels.cy },
    { key: 'py', label: labels.py },
    { key: 'var_py', label: `Variance vs ${labels.py}` },
    { key: 'pct_py', label: `Var % vs ${labels.py}` },
    { key: 'plan', label: labels.plan },
    { key: 'var_plan', label: `Variance vs ${labels.plan}` },
  ]
  const isVariance = measure === 'var_py' || measure === 'var_plan' || measure === 'pct_py'
  const show = (value: number | null) =>
    measure === 'pct_py' ? percent(value) : value === null ? '—' : amount(value, labels.money)
  const toneOf = (value: number | null) => (isVariance && value !== null ? tone(value) : '')
  const toggle = (node: GroupNode<MonthFigures>) => {
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(node.id)) next.delete(node.id)
      else next.add(node.id)
      return next
    })
  }

  return (
    <Card
      title="Month by month"
      subtitle="The workbook's Month by Month tabs. Pick the rows and the number; open a row to go a level down."
    >
      <div className="rcp-controls">
        <div className="rcp-switch" role="group" aria-label="Rows">
          {MONTH_LAYOUTS.map((candidate) => (
            <button
              key={candidate.key}
              type="button"
              aria-pressed={candidate.key === chosen?.key}
              className={candidate.key === chosen?.key ? 'srp-chip is-on' : 'srp-chip'}
              onClick={() => {
                setLayout(candidate.key)
                setOpen(new Set())
              }}
            >
              {candidate.label}
            </button>
          ))}
        </div>
        <div className="rcp-switch" role="group" aria-label="Number">
          {measures.map((candidate) => (
            <button
              key={candidate.key}
              type="button"
              aria-pressed={candidate.key === measure}
              className={candidate.key === measure ? 'srp-chip is-on' : 'srp-chip'}
              onClick={() => {
                setMeasure(candidate.key)
              }}
            >
              {candidate.label}
            </button>
          ))}
        </div>
      </div>
      {tree.length === 0 || monthsShown.length === 0 ? (
        <Empty label="Nothing in these filters" />
      ) : (
        <div className="srp-tall srp-scroll">
          <table className="srp-table rcp-table rcp-months">
            <thead>
              <tr>
                <th>
                  {levels[0] === 'sku'
                    ? 'SKU'
                    : levels[0] === 'end_user'
                      ? 'End user'
                      : levels[0] === 'product_line'
                        ? 'Product line'
                        : 'Ship-to'}
                </th>
                {monthsShown.map((m) => (
                  <th key={m} className="cell-number">
                    {MONTHS[m]}
                  </th>
                ))}
                <th className="cell-number">Total</th>
              </tr>
            </thead>
            <tbody>
              {visible(tree, open).map((node) => {
                const total = cell(node.figures, measure, monthsShown)
                return (
                  <tr key={node.id}>
                    <td className="srp-wrap" style={{ paddingLeft: `${String(0.6 + node.depth * 1.1)}rem` }}>
                      {node.children.length > 0 ? (
                        <button
                          type="button"
                          className="srp-link rcp-toggle"
                          aria-expanded={open.has(node.id)}
                          onClick={() => {
                            toggle(node)
                          }}
                        >
                          <span aria-hidden>{open.has(node.id) ? '▾' : '▸'}</span> {node.label}
                        </button>
                      ) : (
                        <span className="rcp-leaf">{node.label}</span>
                      )}
                    </td>
                    {monthsShown.map((m) => {
                      const value = cell(node.figures, measure, [m])
                      return (
                        <td key={m} className={`cell-number ${toneOf(value)}`}>
                          {show(value)}
                        </td>
                      )
                    })}
                    <td className={`cell-number srp-strong ${toneOf(total)}`}>{show(total)}</td>
                  </tr>
                )
              })}
              <tr className="srp-total">
                <td>Grand total</td>
                {monthsShown.map((m) => {
                  const value = cell(grand, measure, [m])
                  return (
                    <td key={m} className={`cell-number ${toneOf(value)}`}>
                      {show(value)}
                    </td>
                  )
                })}
                <td className={`cell-number ${toneOf(cell(grand, measure, monthsShown))}`}>
                  {show(cell(grand, measure, monthsShown))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
